import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/solid";
import { listMySkills, deleteSkill } from "../../api/advisorApi";

const Card = ({ children, className = "" }) => (
  <div
    className={`p-5 sm:p-6 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark ${className}`}
  >
    {children}
  </div>
);

const STATUS_STYLES = {
  draft:   "bg-amber-500/15 text-amber-500",
  live:    "bg-emerald-500/15 text-emerald-500",
  retired: "bg-gray-500/15 text-gray-500",
};

const RISK_STYLES = {
  low:  "bg-sky-500/15 text-sky-500",
  mid:  "bg-indigo-500/15 text-indigo-500",
  high: "bg-rose-500/15 text-rose-500",
};

const SkillList = () => {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setSkills(await listMySkills());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onDelete = async (skill) => {
    const verb = skill.status === "live" ? "retire" : "delete";
    if (!window.confirm(`${verb[0].toUpperCase() + verb.slice(1)} "${skill.name}"?`)) return;
    await deleteSkill(skill.id);
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Skills</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-2xl">
            A skill is a strategy: target allocation, risk band, and the AI guidance our
            specialists use when generating recommendations for your subscribers.
          </p>
        </div>
        <Link
          to="/advisor/skills/new"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-500 whitespace-nowrap"
        >
          <PlusIcon className="w-4 h-4" />
          New skill
        </Link>
      </div>

      {loading ? (
        <Card>Loading…</Card>
      ) : skills.length === 0 ? (
        <Card>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            You haven't authored a skill yet. Create one to start building a plan.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {skills.map((s) => (
            <Card key={s.id}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-lg">{s.name}</h3>
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${STATUS_STYLES[s.status] || "bg-gray-500/15 text-gray-500"}`}>
                      {s.status}
                    </span>
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${RISK_STYLES[s.risk_band] || "bg-gray-500/15 text-gray-500"}`}>
                      risk: {s.risk_band}
                    </span>
                    <span className="text-[10px] text-gray-500">v{s.version}</span>
                  </div>
                  {s.summary && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{s.summary}</p>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Link
                    to={`/advisor/skills/${s.id}`}
                    className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-white/15 text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/5"
                  >
                    Edit
                  </Link>
                  <button
                    type="button"
                    onClick={() => onDelete(s)}
                    className="px-3 py-1.5 rounded-lg text-red-500 hover:bg-red-500/10 text-sm font-medium flex items-center gap-1"
                  >
                    <TrashIcon className="w-4 h-4" />
                    {s.status === "live" ? "Retire" : "Delete"}
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default SkillList;
